import { TextInput } from "@cmp-src/form/TextInput";
import { SelectTrigger } from "@cmp-src/primitives/Select";
import { GenericSelect } from "@cmp-src/primitives/Select/select-controls/GenericSelect";
import { IResource, ISchedule, IUserFull } from "@gettactic/api";
import { UseDisclosureReturn, cn, useDisclosure } from "@gettactic/helpers";
import { getNowDate } from "@gettactic/helpers/src/date-time/getNowDate";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import { isTacticAppWebView } from "@gettactic/helpers/src/isTacticAppWebView";
import { isResourceAvailable } from "@gettactic/helpers/src/resources/isResourceAvailable";
import { useAuthenticated, useUserListPaginated } from "@gettactic/hooks";
import { useSettings } from "@gettactic/hooks";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Card } from "@v2/components/display/Card/Card";
import { MobileTopMargin } from "@v2/components/display/Card/classes";
import { VirtualizedList } from "@v2/components/display/VirtualizedList/VirtualizedList";
import { DivisionSelector } from "@v2/components/divisions/DivisionSelector/DivisionSelector";
import { FloorSelector } from "@v2/components/divisions/FloorSelector/FloorSelector";
import { CalendarInput } from "@v2/components/form/calendar/CalendarInput/CalendarInput";
import { UserLabel } from "@v2/components/labels/UserLabel/UserLabel";
import { AnimatedTabs } from "@v2/components/tabs/AnimatedTabs/AnimatedTabts";
import { StartEndTimePicker } from "@v2/components/time/StartEndTimePicker/StartEndTimePicker";
import { Switch } from "@v2/components/ui/Switch";
import { Button } from "@v2/components/ui/button";
import { ArrowDownIcon, CloseIcon, FilterIcon, SearchIcon } from "@v2/icons";
import { MobileDrawer } from "@v2/mobile/MobileDrawer/MobileDrawer";
import { MapFiltersCardSearchNoResults } from "@webV2/components/cards/MapFiltersCard/MapFiltersCardSearchNoResults";
import { useMap } from "@webV2/map/MapProvider";
import { MenuIcon } from "lucide-react";
import { useRouter } from "next/router";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { EmbeddedScheduleButton } from "./EmbeddedScheduleButton";
import { getPlaceholderText, getResourcesVirtualizedListTab } from "./helpers";

const FiltersTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
    hasValue?: boolean;
    count?: number;
  }
>(({ className, children, hasValue = false, count = 0, ...props }, ref) => {
  const { isBreakpoint } = useBreakpoint();
  const isMd = isBreakpoint("md");

  /*  if (!isMd) {
      return <button>HERE</button>;
    }*/

  return (
    <SelectTrigger
      ref={ref}
      className={cn(
        "flex h-10 min-h-[42px] w-full cursor-pointer items-center gap-2 rounded-lg border-1 border-neutral-200 bg-background font-bold text-sm shadow-lg ring-offset-background placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 md:border-0 md:shadow-none",
        className
      )}
      {...props}
      showArrow={false}
    >
      <Button
        variant={"ghost"}
        size={"sm"}
        className={"text-neutral-1000 md:-mr-[22px] hover:bg-transparent"}
      >
        {!!count && (
          <div
            className={
              "-mt-6 -mr-6 absolute h-6 w-6 rounded-full bg-secondary p-1 text-xs"
            }
          >
            {count}
          </div>
        )}
        <FilterIcon className={"size-4"} />
      </Button>
    </SelectTrigger>
  );
});
FiltersTrigger.displayName = "EmployeeSelectTrigger";

const classes = {
  FloorAndTimeWrapper: "flex gap-2",
  TabsContent: "relative data-[state=active]:flex flex-1",
  VirtualizedList: "absolute inset-0",
  ListItem: "hover:cursor-pointer select-none h-[86px]",
  TabsContainer: "h-full flex flex-col flex-1",
  TabsList: "flex items-center justify-between",
  TabContentScrollArea: "!absolute inset-0",
  SearchWrapper:
    "mb-2 active:-outline-offset-2 py-2 transition-all duration-300 ease-in-out data-[state=closed]:max-h-0 data-[state=closed]:opacity-0 data-[state=closed]:py-0 data-[state=open]:max-h-20 data-[state=open]:opacity-100",
  SearchIcon: "size-4 text-neutral-600",
  SearchCloseIcon: "size-4 text-neutral-600 hover:text-neutral-1000"
};

export const MapFiltersCardClasses = classes;

export interface MapFiltersCardProps {
  dayViewDisclosure?: UseDisclosureReturn;
  isCompact?: boolean;
  isModal?: boolean;
}

export const MapFiltersCard: React.FC<MapFiltersCardProps> = ({
  dayViewDisclosure,
  isCompact,
  isModal
}) => {
  const { t } = useTranslation("common");
  const searchRef = React.useRef<HTMLInputElement>(null);
  const router = useRouter();
  // const [searchText, setSearchText] = React.useState("");

  const {
    currentOffice,
    changeOffice,
    floors,
    floor,
    setFloor,
    changeStartDate,
    changeEndTime,
    startDate,
    endDate,
    timeMode,
    setTimeMode,
    resources: allResources,
    schedulable,
    events,
    amenities,
    applyAmenitiesFilter,
    amenitiesFilter,
    isMapEditing,
    userFocusDisclosure,
    mapFiltersDisclosure,
    setLocateResourceId,
    filterQuery: searchText,
    setFilterQuery: setSearchText,
    defaultTab: selectedTab,
    setSection: setSelectedTab
  } = useMap();

  const { isBreakpoint } = useBreakpoint();
  const isMd = isBreakpoint("md");

  const [amenitiesFilterText, setAmenitiesFilterText] =
    React.useState<string>("");

  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();

  const { setUserContext } = useAuthenticated();
  const { isOrgPrivacyEnabled } = useSettings();

  const setSelectedDate = (date: Date | Date[] | undefined) => {
    const dates = Array.isArray(date) ? date : [date];
    if (dates.length === 1 && dates[0]) {
      // Preserve the current time when changing the date
      const newDate = dates[0];
      if (startDate) {
        newDate.setHours(startDate.getHours());
        newDate.setMinutes(startDate.getMinutes());
        newDate.setSeconds(startDate.getSeconds());
        newDate.setMilliseconds(startDate.getMilliseconds());
      }
      changeStartDate(newDate);
      setTimeMode("manual");
    } else {
      // TODO: Handle multiple dates
      console.warn("Multiple dates selected:", dates);
      changeStartDate(dates[0] || getNowDate());
    }
  };

  const onFloorChange = (floorId?: string) => {
    const foundFloor = floors?.find((floor) => floor.id === floorId);
    setFloor(foundFloor || null);
  };

  // const [selectedTab, setSelectedTab] = React.useState<
  //   "people" | "rooms" | "desks" | "other"
  // >(defaultTab || (isOrgPrivacyEnabled ? "rooms" : "people"));
  const [showHighlighter, setShowHighlighter] = React.useState(true);
  const searchDisclosure = useDisclosure();

  // API should always receive the query (from local input / URL)
  const { allPages: allUsers, isFetching } = useUserListPaginated(
    searchText,
    300
  );

  const handleTabChange = (value: string) => {
    // If search icon tab is clicked, open search instead
    if (value === "search-tab") {
      handleSearchOpenChange(true);
      return;
    }

    // Temporarily hide highlighter during tab animation
    setShowHighlighter(false);
    setSelectedTab(value as "people" | "rooms" | "desks" | "other");

    // Re-enable highlighter after animation completes
    setTimeout(() => {
      setShowHighlighter(true);
    }, 350); // Wait for spring animation to complete
  };

  const users = React.useMemo(() => {
    const scheduleUsers: Record<
      string,
      { user: IUserFull; schedules: ISchedule[] }
    > = {};
    for (const schedulableItem of schedulable || []) {
      const schedules = schedulableItem.schedules || [];
      for (const schedule of schedules) {
        if (schedule.slots) {
          for (const slot of schedule.slots) {
            if (slot.user && !scheduleUsers[slot.user.id]) {
              scheduleUsers[slot.user.id] = { user: slot.user, schedules: [] };
            }
            scheduleUsers[slot.user.id].schedules = [
              ...(scheduleUsers[slot.user.id].schedules || []),
              {
                ...schedule,
                resource: schedulableItem,
                start: schedule.start,
                end: schedule.end,
                slots: schedule.slots
              }
            ];
          }
        }
      }
    }

    const allUsers = Object.values(scheduleUsers);

    if (searchText) {
      return allUsers?.filter((user) => {
        return (
          user.user.name?.toLowerCase().includes(searchText.toLowerCase()) ||
          user.user.email?.toLowerCase().includes(searchText.toLowerCase()) ||
          user.user.title?.toLowerCase().includes(searchText.toLowerCase())
        );
      });
    }

    return allUsers;
  }, [schedulable, allUsers, searchText]);

  const resources = React.useMemo(() => {
    let availableResources =
      (searchText
        ? allResources?.filter((resource) => {
            return (
              resource.name.toLowerCase().includes(searchText.toLowerCase()) ||
              resource.tags?.some((tag) =>
                tag.name.toLowerCase().includes(searchText.toLowerCase())
              )
            );
          })
        : allResources) || [];

    availableResources = availableResources
      .filter((resource) => {
        const schedulableResource = schedulable?.find(
          (schedulableResource) => schedulableResource.id === resource.id
        );
        return isResourceAvailable(
          authenticatedUser,
          { ...resource, ...schedulableResource },
          {
            authenticatedUser,
            officeTimeZone: currentOffice?.time_zone ?? "UTC",
            office: currentOffice,
            resourceType: resource.type,
            allowReserveOnBehalf: false,
            meetingsDisabled: false,
            startDate: new Date()
          }
        );
      })
      .sort((a, b) => {
        const aHasFav = a.favorite_id != null;
        const bHasFav = b.favorite_id != null;
        return aHasFav === bHasFav ? 0 : aHasFav ? -1 : 1;
      });

    return {
      desks:
        availableResources?.filter((resource) => resource.type === "desk") ||
        [],
      rooms:
        availableResources?.filter(
          (resource) => resource.type === "meeting_room"
        ) || [],
      other:
        availableResources?.filter(
          (resource) =>
            resource.type !== "desk" && resource.type !== "meeting_room"
        ) || []
    };
  }, [allResources, searchText, schedulable]);

  const handleSearchOpenChange = (isOpen?: boolean) => {
    if (isOpen) {
      searchDisclosure.open();
      setShowHighlighter(true);
      setTimeout(() => {
        searchRef.current?.focus();
      }, 10);
      return;
    }
    searchDisclosure.close();
    setSearchText("");
    setShowHighlighter(true);
  };

  const handleSearchTextChange = (value: string) => {
    setSearchText(value);
    // Ensure highlighter is shown when typing
    if (value && !showHighlighter) {
      setShowHighlighter(true);
    }
  };

  const tabsRef = React.useRef<HTMLInputElement>(null);

  // useUrlQuerySync(
  //   {
  //     searchText,
  //     section: selectedTab
  //   },
  //   (paramsFromUrl) => {
  //     if (paramsFromUrl.section && paramsFromUrl.section !== selectedTab) {
  //       const requestedSection = String(paramsFromUrl.section);
  //       // If privacy is enabled and user tries to access people tab, redirect to rooms instead
  //       const validSection =
  //         isOrgPrivacyEnabled && requestedSection === "people"
  //           ? "rooms"
  //           : requestedSection;
  //       setSelectedTab(validSection as "people" | "rooms" | "desks" | "other");
  //     }

  //     if (paramsFromUrl.searchText && paramsFromUrl.searchText !== searchText) {
  //       searchDisclosure.open();
  //       setSearchText(String(paramsFromUrl.searchText));
  //     }
  //   },
  //   router.isReady && (floors?.length || 0) > 0 && !isModal,
  //   false,
  //   true
  // );

  const onResourceClick = (resource: IResource | null) => {
    events.trigger("onFocusResource", resource);
    mapFiltersDisclosure.close();
  };

  const setResourceIdHovering = (resource: IResource | null) => {
    events.trigger("onHighlightResource", resource);
  };

  const setUserIdHovering = (userId: string | null) => {
    const resources = schedulable?.filter((resource) => {
      return (
        resource.schedules?.some((schedule) =>
          schedule.slots?.some((slot) => slot.user?.id === userId)
        ) || false
      );
    });
    events.trigger("onHighlightUserResource", resources || null);
  };

  const amenityOptions = React.useMemo(() => {
    return amenities.map((amenity) => ({
      value: amenity.id,
      label: amenity.name
    }));
  }, [amenities]);

  const filtersSelect = (
    <GenericSelect
      options={amenityOptions}
      onCancel={() => applyAmenitiesFilter([])}
      onChange={(values) => {
        // We don't need to update local state since we're just passing through
        // No-op to prevent unwanted onChange calls during selection
      }}
      onInputChange={setAmenitiesFilterText}
      filter={amenitiesFilterText}
      isMultiSelect={true}
      showApplyButton={true}
      onApply={(values) => {
        applyAmenitiesFilter(Array.isArray(values) ? values : [values]);
      }}
      defaultValue={amenitiesFilter.map((tag) => tag.id)}
      triggerComponent={<FiltersTrigger count={amenitiesFilter.length} />}
      applyOnClick={false}
      triggerClassName="font-bold text-muted-foreground"
      closeOnApply
      showSearch
    />
  );

  const floorTabs = (
    <>
      {floor ? (
        <AnimatedTabs
          contentRef={tabsRef}
          onValueChange={handleTabChange}
          value={selectedTab || "people"}
          className={"flex h-full flex-1 flex-col"}
          extraAction={
            <div
              className={"flex flex-1 items-center justify-end"}
              onClick={(e) => e.stopPropagation()}
            >
              {isMd && amenities.length > 0 ? filtersSelect : undefined}
            </div>
          }
          tabs={[
            // ...(isSearchInitialized.state
            //   ? [
            //       {
            //         value: "all",
            //         label: "All",
            //         content: (
            //           <MapSearchResults
            //             users={users?.map((user) => user.user) || []}
            //             resources={resources}
            //             setResourceIdHovering={setResourceIdHovering}
            //             onBook={onResourceClick}
            //             searchText={searchText}
            //           />
            //         )
            //       }
            //     ]
            //   : []),

            // Hide People tab when privacy is enabled to protect user information
            ...(isOrgPrivacyEnabled
              ? []
              : [
                  {
                    value: "people",
                    label: t("terms.people"),
                    content: !users.length ? (
                      searchText.length > 0 ? (
                        <MapFiltersCardSearchNoResults
                          target={"people"}
                          searchText={searchText}
                          floorName={floor?.name || ""}
                        />
                      ) : (
                        <div className="text-center text-gray-500 text-md">
                          {getPlaceholderText("people")}
                        </div>
                      )
                    ) : (
                      <VirtualizedList
                        itemHeight={86}
                        scrollAreaProps={{
                          className: classes.TabContentScrollArea
                        }}
                        isLoading={isFetching}
                      >
                        {users.map(({ user, schedules }) => (
                          <UserLabel
                            className={classes.ListItem}
                            user={user}
                            key={user.id}
                            onMouseEnter={() => {
                              setUserIdHovering(user.id);
                            }}
                            onClick={() => {
                              userFocusDisclosure?.open(user);
                            }}
                            onMouseLeave={() => setUserIdHovering(null)}
                            schedules={schedules}
                            allowFavorite
                            allowTessa
                          />
                        ))}
                      </VirtualizedList>
                    ),
                    className: classes.TabsContent
                  }
                ]),
            ...[
              getResourcesVirtualizedListTab({
                value: "rooms",
                label: t("terms.rooms"),
                resources: resources.rooms,
                setResourceIdHovering,
                onReserve: onResourceClick,
                searchText,
                floorName: floor?.name || ""
              }),
              getResourcesVirtualizedListTab({
                value: "desks",
                label: t("terms.desks"),
                resources: resources.desks,
                setResourceIdHovering,
                onReserve: onResourceClick,
                searchText,
                floorName: floor?.name || ""
              }),
              getResourcesVirtualizedListTab({
                value: "other",
                label: t("terms.other"),
                resources: resources.other,
                setResourceIdHovering,
                onReserve: onResourceClick,
                searchText,
                floorName: floor?.name || ""
              })
            ],
            // Search icon tab - only visible when search is not active
            ...(isMd &&
            !searchDisclosure.isOpen
              ? [
                  {
                    value: "search-tab",
                    className: "py-0",
                    label: <SearchIcon className={"size-4 text-neutral-700"} />,
                    content: <></>
                  }
                ]
              : [])
          ]}
          defaultValue={isOrgPrivacyEnabled ? "rooms" : "people"}
        />
      ) : (
        <div className={"flex h-full w-full items-center justify-center"}>
          Select a Floor
        </div>
      )}
      {/* {tabsRef && searchDisclosure.isOpen && searchText.length > 0 && showHighlighter && (
        <TextHighlighter 
          key={`${selectedTab}-${searchText}`}
          containerRef={tabsRef} 
          searchTerm={searchText} 
        />
      )} */}
    </>
  );

  const isMobileApp = isTacticAppWebView();

  const [isInternalModalOpen, setIsInternalModalOpen] = useState(false);
  const onInternalModalOpenChange = (showing: boolean) => {
    setIsInternalModalOpen(showing);
  };
  return (
    <>
      <div
        className={cn(
          isMobileApp ? "top-14" : "top-0",
          `fixed right-0 left-0 border-b-neutral-200 px-4 pb-2 md:hidden md:border-b md:bg-white [.v2-embedded-map_&]:flex [.v2-embedded-map_&]:px-4 [.v2-embedded-map_&]:py-2 ${MobileTopMargin}`,
          isInternalModalOpen ? "z-[100]" : "z-sticky"
        )}
      >
        <div className={"flex w-full items-center gap-2"}>
          <div className={"w-full min-w-0 max-w-[260px] md:w-auto"}>
            <DivisionSelector
              selectedOffice={currentOffice}
              onSelectOffice={(office) => {
                if (office?.id) {
                  changeOffice(office.id);
                }
              }}
              triggerProps={{ className: "rounded-v2" }}
              trigger={
                isMd ? (
                  <button
                    className={
                      "group inline-flex w-full items-center justify-start gap-2 rounded-v2"
                    }
                  >
                    <span className={"min-w-0 flex-1 truncate text-left"}>
                      {currentOffice.name}{" "}
                    </span>
                    <ArrowDownIcon
                      className={"size-4 flex-shrink-0 transition-opacity"}
                    />
                  </button>
                ) : undefined
              }
            />
          </div>
          <div className={"flex items-center gap-2"}>
            <div>
              <FloorSelector
                className={
                  "mt-0 min-h-auto max-w-[120px] bg-white py-0 shadow-lg"
                }
                floors={floors || []}
                value={floor?.id || "0"}
                onChangeFloor={onFloorChange}
                allowEmpty
              />
            </div>
            <div>{filtersSelect}</div>
            <div className={"fixed bottom-16 left-3"}>
              <Button
                size={"sm"}
                className={
                  "border-1 border-neutral-200 bg-white text-neutral-1000 shadow-lg"
                }
                onClick={mapFiltersDisclosure.toggle}
              >
                <MenuIcon className={"size-4"} /> List View
              </Button>
            </div>
          </div>
        </div>
        <div className={"relative flex items-center gap-2"}>
          {isCompact && (
            <EmbeddedScheduleButton
              onOverlayChange={onInternalModalOpenChange}
              currentOffice={currentOffice}
            />
          )}
          <CalendarInput
            mode={"single"}
            onSelect={setSelectedDate}
            selectedDate={startDate || getNowDate()}
            //allowMultipleSwitch
            setValueOnApply
            withArrows={true}
          />
          <StartEndTimePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={changeStartDate}
            onEndDateChange={changeEndTime}
            mode={timeMode}
            onModeChange={setTimeMode}
            className={
              "[.v2-embedded-map_&]:w-[230px] [.v2-embedded-map_&]:min-w-[230px]"
            }
            allowLive
          />
        </div>
      </div>
      {isMd && !isCompact ? (
        <div className={cn("hidden h-full md:block", "md:block")}>
          <Card
            className={"relative flex h-full flex-col"}
            titleWrapperClassName={"gap-3"}
            contentProps={{
              className: "flex justify-between flex-col flex-1"
            }}
            title={
              isMd && !isMapEditing ? (
                <DivisionSelector
                  selectedOffice={currentOffice}
                  onSelectOffice={(office) => {
                    if (office?.id) {
                      changeOffice(office.id);
                    }
                  }}
                  trigger={
                    <button
                      className={
                        "group inline-flex w-full max-w-full items-center justify-start gap-1"
                      }
                    >
                      <span className={"min-w-0 truncate text-left"}>
                        {currentOffice.name}{" "}
                      </span>
                      <ArrowDownIcon
                        className={"size-4 flex-shrink-0 transition-opacity"}
                      />
                    </button>
                  }
                />
              ) : undefined
            }
            action={
              isMd ? (
                !isMapEditing && dayViewDisclosure ? (
                  <Switch
                    onCheckedChange={dayViewDisclosure.onOpenChange}
                    checked={dayViewDisclosure.isOpen}
                    variant={"squared"}
                    size={"xs"}
                  />
                ) : undefined
              ) : undefined
            }
          >
            {isMapEditing ? (
              <div
                className={
                  "pointer-events-none select-none py-2 text-center text-md text-neutral-800"
                }
              >
                This view is disabled when editing the map. Please exit edit
                mode to use it.
              </div>
            ) : (
              <>
                <div className={"hidden md:block"}>
                  <CalendarInput
                    mode={"single"}
                    onSelect={setSelectedDate}
                    selectedDate={startDate || getNowDate()}
                    //allowMultipleSwitch
                    setValueOnApply
                    withArrows={true}
                  />
                  <div className={classes.FloorAndTimeWrapper}>
                    <FloorSelector
                      className={"max-w-[120px]"}
                      floors={floors || []}
                      value={floor?.id}
                      onChangeFloor={onFloorChange}
                      allowEmpty
                    />
                    <div className={"w-full"}>
                      <StartEndTimePicker
                        startDate={startDate}
                        endDate={endDate}
                        onStartDateChange={changeStartDate}
                        onEndDateChange={changeEndTime}
                        mode={timeMode}
                        onModeChange={setTimeMode}
                        allowLive
                      />
                    </div>
                  </div>
                  <div
                    data-state={searchDisclosure.isOpen ? "open" : "closed"}
                    className={classes.SearchWrapper}
                  >
                    <TextInput
                      ref={searchRef}
                      onKeyDown={(event) => {
                        if (event.key === "Escape") {
                          handleSearchOpenChange(false);
                        }
                      }}
                      placeholder={"Search..."}
                      leftIcon={<SearchIcon className={classes.SearchIcon} />}
                      rightSlot={[
                        <button key={"clear-icon"}>
                          <CloseIcon
                            className={classes.SearchCloseIcon}
                            onClick={handleSearchOpenChange.bind(null, false)}
                          />
                        </button>
                      ]}
                      onChange={(event) =>
                        handleSearchTextChange(event.target.value)
                      }
                      className="rounded-v2-md"
                      value={searchText}
                    />
                  </div>
                </div>

                {floorTabs}
              </>
            )}
          </Card>
        </div>
      ) : undefined}
      {!isMd || isCompact ? (
        <MobileDrawer
          isOpen={mapFiltersDisclosure.isOpen}
          onOpenChange={mapFiltersDisclosure.onOpenChange}
          revealGap={200}
          size={"full"}
          showBackdrop={false}
          scrollableContent={false}
          containerClassName={"static"}
          contentUnderGrabber
          dragOnTileOnly
        >
          <div className={"absolute top-10 right-3 bottom-0 left-4"}>
            {floorTabs}
          </div>
        </MobileDrawer>
      ) : undefined}
    </>
  );
};
